import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { OrderRating } from 'api/orders'
import FeedbackRatingIcon from 'components/FeedbackRatingIcon'
import FeedbackResolvedByCell from 'components/FeedbackResolvedByCell'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
import { getOrderRatingViewPath } from 'utilities/orderRatingViewPath'

const t = buildTranslate('users.view.activity.orders')

type Props = {
  rating: OrderRating | null
}

// EN dash (U+2013), matching the prototype's `<td class="muted t-small">–</td>`
// verbatim (S6, RecoveryAI Customer Profile Page design spec). NOT the same
// glyph as components/FeedbackTable's own EMPTY_CELL_PLACEHOLDER, which is an
// EM dash — the prototype specifically uses the shorter one here. This
// constant is local to this file; do not go changing FeedbackTable's
// placeholder to match it.
const UNRATED_PLACEHOLDER = '–'

// S4 (RecoveryAI Customer Profile Page, R4-prototype.md): a rated order
// stacks its thumbs icon directly above either the shared RecoveryAI badge or
// plain "Resolved by <Name>" text, in its own Rating column left of Details.
//
// The AI branch reuses components/FeedbackResolvedByCell as-is — the SAME
// component the Order feedback tab (AI-1288) and the Order feedback inbox
// (Reports > Order feedback > Details) already render for a RecoveryAI
// resolution, so this surface can't drift onto a second, hand-rolled badge.
// The operator branch is NOT routed through that same cell: its default
// rendering (a plain grey90 name, no "Resolved by" prefix) is styled for a
// wider table column and doesn't match S4's muted, prefixed copy here, and
// there's no ticket requirement to unify that half — see R4's Done-contract
// §4 ("Resolved by is a two-shape pattern, not one; build both variants as
// plain inline content, not a forced shared component").
//
// REVERSED (S6): an unrated order used to render nothing here — no dash, no
// icon — reasoning from this ticket's own wording ("shows nothing new"),
// which this comment used to say overrode S4's mock of a muted dash
// placeholder. That reasoning is retired: a 1:1 match with the pinned
// prototype wins over the ticket's phrasing, and the prototype's own mock IS
// a dash, not a blank cell. It renders the en dash above, grey50 at 14px,
// same as every other muted table cell — no icon, no link (there's no rating
// to open). The table is table-layout:fixed (Table.tsx), so this never shifts
// any other column's width either way.
const RatingCell: React.FC<Props> = ({ rating }: Props) => {
  const [css] = useStyletron()

  const cellStackClass = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    // A link wrapping mixed content (icon + badge/text), not a text link — the
    // anchor's own default color/underline must not bleed onto either child,
    // both of which already carry their own color.
    textDecoration: 'none',
  })

  if (!rating?.rating) {
    return (
      <Text.BodyText4 color="grey50" className="line-height-20">
        {UNRATED_PLACEHOLDER}
      </Text.BodyText4>
    )
  }

  const { resolved_by: resolvedBy } = rating

  // Shared with the Order feedback tab and the inbox: a conversation-backed
  // rating opens the RecoveryAI case, everything else (non-RAI, dismissed, or
  // unresolved) opens the Reports rating detail. Keyed on the OrderRating
  // hashid either way — a raw integer id 404s on both routes.
  const viewPath = getOrderRatingViewPath({
    id: rating.id,
    conversationId: rating.conversation_id,
  })

  return (
    <Link to={viewPath} className={cellStackClass}>
      <FeedbackRatingIcon rating={rating.rating} />
      {resolvedBy.kind === 'ai' ? (
        <FeedbackResolvedByCell resolvedBy={resolvedBy} subtleResolvedBadge />
      ) : (
        resolvedBy.kind === 'operator' &&
        resolvedBy.name && (
          <Text.BodyText5 color="grey50">
            {t('resolved_by', { name: resolvedBy.name })}
          </Text.BodyText5>
        )
      )}
    </Link>
  )
}

export default RatingCell
